import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import * as Dtos from "src/dtos/Tmd.dtos";
import type { AppRouteHandle, AppRouteObject, RouteMetadataContext } from "./types";

/**
 * Metadata owned by the feature route modules.  Keeping this in a small
 * decorator lets the route trees remain readable while making every feature
 * route explicit before the application-wide defaults are applied.
 */
type FeatureRoute = "institution" | "laboratory" | "personnel" | "project";

const topicPermissions: Record<string, Dtos.Permission | undefined> = {
    institutions: Dtos.Permission.InstitutionView,
    institution: Dtos.Permission.InstitutionView,
    "institution-items": Dtos.Permission.InstitutionItemView,
    "institution-personnels": Dtos.Permission.InstitutionPersonnelView,
    "institution-personnel": Dtos.Permission.InstitutionPersonnelView,
    "institution-laboratories": Dtos.Permission.LabInstitutionView,
    "institution-laboratory": Dtos.Permission.LabInstitutionView,
    "institution-notes": Dtos.Permission.InstitutionNoteView,
    "institution-procedures": Dtos.Permission.InstitutionProcedureView,
    "institution-procedure": Dtos.Permission.InstitutionProcedureView,
    "institution-finance": Dtos.Permission.InstitutionFinanceView,
    "institution-contract": Dtos.Permission.InstitutionContractView,
    "institution-codes": Dtos.Permission.InstitutionCodeView,
    "institution-code": Dtos.Permission.InstitutionCodeView,
    "institution-trials": Dtos.Permission.TrialInstitutionView,
    labs: Dtos.Permission.LabView,
    lab: Dtos.Permission.LabDetailsView,
    "lab-departments": Dtos.Permission.LabDepartmentView,
    "lab-department": Dtos.Permission.LabDepartmentView,
    "lab-personnels": Dtos.Permission.LabPersonnelView,
    "lab-personnel": Dtos.Permission.LabPersonnelView,
    "lab-institutions": Dtos.Permission.LabInstitutionView,
    "lab-institution": Dtos.Permission.LabInstitutionView,
    "lab-finance": Dtos.Permission.LabFinanceView,
    "lab-contract": Dtos.Permission.LabContractView,
    personnels: Dtos.Permission.PersonnelView,
    personnel: Dtos.Permission.PersonnelView,
    "personnel-by-roles": Dtos.Permission.PersonnelRoleView,
    "personnel-training-certification": Dtos.Permission.TrainingCertificationsView,
    "personnel-roles": Dtos.Permission.PersonnelRoleView,
    "personnel-role": Dtos.Permission.PersonnelRoleView,
    "personnel-permissions": Dtos.Permission.PersonnelRoleView,
    "personnel-active-directory": Dtos.Permission.PersonnelSecurityView,
    "personnel-notes": Dtos.Permission.PersonnelNoteView,
    fellowships: Dtos.Permission.FellowshipView,
    fellowship: Dtos.Permission.FellowshipView,
    projects: Dtos.Permission.ProjectView,
    project: Dtos.Permission.ProjectView,
    "project-items": Dtos.Permission.ProjectItemsView,
    "project-investigator": Dtos.Permission.ProjectInvestigatorsView,
    discussions: Dtos.Permission.ProjectDiscussionView,
    discussion: Dtos.Permission.ProjectDiscussionView,
    note: Dtos.Permission.TrialNoteView,
};

const featureRoots: Record<FeatureRoute, { title: string; icon: typeof FontAwesomeIcons.Duotone.HOME; permission: Dtos.Permission }> = {
    institution: { title: "Institutions", icon: FontAwesomeIcons.Duotone.INSTITUTION, permission: Dtos.Permission.InstitutionView },
    laboratory: { title: "Labs", icon: FontAwesomeIcons.Duotone.FLASKVIAL, permission: Dtos.Permission.LabView },
    personnel: { title: "Personnel", icon: FontAwesomeIcons.Duotone.USER, permission: Dtos.Permission.PersonnelView },
    project: { title: "Projects", icon: FontAwesomeIcons.Duotone.CLIPBOARD, permission: Dtos.Permission.ProjectView },
};

const humanize = (value: string): string => value
    .replace(/^:/, "")
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, character => character.toUpperCase());

const dynamicLabel = (route: FeatureRoute, path: string, context: RouteMetadataContext): string | undefined => {
    if (path.includes(":institutionLaboratoryId")) return context.labels.laboratoryName || "Laboratory";
    if (path.includes(":institutionCodeId")) return context.labels.institutionCodeName || "Code";
    if (path.includes(":institutionProcedureId")) return context.labels.institutionProcedureName || "Procedure";
    if (path.includes(":laboratoryDepartmentId")) return context.labels.laboratoryDepartmentName || "Department";
    if (path.includes(":laboratoryPersonnelId")) return context.labels.laboratoryPersonnelName || "Personnel";
    if (path.includes(":personnelRoleId")) return context.labels.personnelRoleName || "Role";
    if (path.includes(":fellowshipId")) return context.labels.fellowshipName || "Fellowship";
    if (path.includes(":projectInvestigatorId")) return context.labels.investigatorName || "Investigator";
    if (path.includes(":discussionId")) return context.labels.discussionName || "Discussion";
    if (path.includes(":noteId")) return context.labels.noteName || "Note";
    if (route === "institution" && path.includes(":institutionId")) return context.labels.institutionName || "Institution";
    if (route === "laboratory" && path.includes(":laboratoryId")) return context.labels.laboratoryName || "Lab";
    if (route === "personnel" && path.includes(":personnelId")) return context.labels.personnelName || "Personnel";
    if (route === "project" && path.includes(":projectId")) return context.labels.projectName || "Project";
    return undefined;
};

const routeAccess = (topic: string | undefined, fullPath: string): AppRouteHandle["access"] => {
    const permission = topic === "note"
        ? fullPath.includes("/institutions/")
            ? Dtos.Permission.InstitutionNoteView
            : fullPath.includes("/personnel/")
                ? Dtos.Permission.PersonnelNoteView
                : topicPermissions[topic]
        : topic ? topicPermissions[topic] : undefined;
    if (permission === undefined) return null;
    return {
        permission,
        trialParam: fullPath.includes(":trialId") ? "trialId" : undefined,
        institutionParam: fullPath.includes(":institutionId")
            ? "institutionId"
            : fullPath.includes(":trialInstitutionId")
                ? "trialInstitutionId"
                : undefined,
    };
};

const topicFromHandle = (handle: AppRouteHandle | undefined): string | undefined => handle?.help?.topicKey;

const featureTitle = (route: FeatureRoute, path: string, topic: string | undefined, context: RouteMetadataContext): string => {
    const label = dynamicLabel(route, path, context);
    const currentSegment = path.split("/").filter(Boolean).at(-1);
    if (label && (currentSegment === "details" || currentSegment?.startsWith(":"))) {
        if (currentSegment === "details") return `${label} · Details`;
        return label;
    }
    if (topic) {
        if (topic === "institutions") return "Institutions";
        if (topic === "labs") return "Labs";
        if (topic === "personnels") return "Personnel";
        if (topic === "projects") return "Projects";
        const topicTitle = topic.replace(/-/g, " ").replace(/\b\w/g, character => character.toUpperCase());
        if (topicTitle) return topicTitle;
    }
    const segment = path.split("/").filter(Boolean).at(-1);
    return segment && !segment.startsWith(":") ? humanize(segment) : "Details";
};

const featureSectionLabel = (path: string, topic: string | undefined): string | undefined => {
    const segment = path.split("/").filter(Boolean).at(-1);
    const labels: Record<string, string> = {
        institutionitems: "Items",
        personnel: "Personnel",
        labs: "Labs",
        notes: "Notes",
        procedures: "Procedures",
        codes: "Codes",
        departments: "Departments",
        institutions: "Institutions",
        roles: "Roles",
        permissions: "Permissions",
        fellowship: "Fellowships",
        discussions: "Discussions",
        investigators: "Investigators",
        trials: "Trials",
        finance: "Finance",
        finances: "Finance",
        contract: "Contract",
        contracts: "Contracts",
        accrual: "Accrual",
        website: "Website",
        datasharing: "Data Sharing",
    };
    return (segment && labels[segment]) || (topic === "discussions" ? "Discussions" : undefined);
};

const featureSectionIcon = (label: string | undefined): typeof FontAwesomeIcons.Duotone.HOME | undefined => {
    if (label === "Details") return FontAwesomeIcons.Duotone.CIRCLEINFO;
    if (label === "Items") return FontAwesomeIcons.Duotone.I;
    if (label === "Personnel") return FontAwesomeIcons.Duotone.USER;
    if (label === "Labs") return FontAwesomeIcons.Duotone.FLASKVIAL;
    if (label === "Institutions") return FontAwesomeIcons.Duotone.INSTITUTION;
    if (label === "Notes") return FontAwesomeIcons.Duotone.NOTESMEDICAL;
    if (label === "Procedures") return FontAwesomeIcons.Duotone.BOOK;
    if (label === "Codes") return FontAwesomeIcons.Duotone.SQUARECODE;
    if (label === "Departments") return FontAwesomeIcons.Duotone.BUILDINGMEMO;
    if (label === "Roles") return FontAwesomeIcons.Duotone.USERSHIELD;
    if (label === "Permissions") return FontAwesomeIcons.Duotone.USERSHIELD;
    if (label === "Discussions") return FontAwesomeIcons.Duotone.COMMENTS;
    return undefined;
};

const featureEntityIcon = (feature: FeatureRoute): typeof FontAwesomeIcons.Duotone.HOME => {
    if (feature === "institution") return FontAwesomeIcons.Duotone.BUILDINGS;
    if (feature === "laboratory") return FontAwesomeIcons.Duotone.BUILDINGWHEAT;
    if (feature === "personnel") return FontAwesomeIcons.Duotone.IDBADGE;
    return FontAwesomeIcons.Duotone.DICED20;
};

const withFeatureRouteMetadata = (routes: AppRouteObject[], feature: FeatureRoute, parentPath = ""): AppRouteObject[] => routes.map(route => {
    const path = route.path;
    const fullPath = path ? `${parentPath}/${path}`.replace(/\/+/g, "/") : parentPath;
    const existing = route.handle ?? {};
    const topic = topicFromHandle(existing);
    const root = parentPath === "" && path === featureRoots[feature].title.toLowerCase() ? featureRoots[feature] : undefined;
    const isRedirect = Boolean(route.index && route.element);
    const isStructural = parentPath !== "" && Boolean(route.children && !route.lazy && !route.element && !route.index);
    const isTopLevelEntityDetails = path === "details" && (
        fullPath === "/institutions/:institutionId/details" ||
        fullPath === "/labs/:laboratoryId/details"
    );
    const title = isRedirect || isStructural
        ? undefined
        : existing.title ?? ((context: RouteMetadataContext) => featureTitle(feature, fullPath, topic, context));
    const breadcrumb = isRedirect || isTopLevelEntityDetails
        ? null
        : existing.breadcrumb !== undefined
            ? existing.breadcrumb
            : {
            label: path === "details"
                ? "Details"
                : featureSectionLabel(path ?? fullPath, topic)
                    ?? title
                    ?? ((context: RouteMetadataContext) => path?.startsWith(":")
                        ? dynamicLabel(feature, fullPath, context) ?? humanize(path.slice(1))
                        : humanize(path ?? fullPath.split("/").filter(Boolean).at(-1) ?? "Details")),
            icon: path === "details"
                ? FontAwesomeIcons.Duotone.CIRCLEINFO
                : path?.startsWith(":")
                    ? featureEntityIcon(feature)
                : featureSectionIcon(featureSectionLabel(path ?? fullPath, topic)) ?? root?.icon,
            url: (context: RouteMetadataContext) => {
                let resolved = fullPath;
                for (const [param, value] of Object.entries(context.params)) {
                    if (value) resolved = resolved.replace(`:${param}`, value);
                }
                if (isStructural && path?.startsWith(":")) return `${resolved}/details`;
                return resolved || "/";
            },
            };
    const navigation = existing.navigation ?? (root
        ? {
            title: root.title,
            icon: root.icon,
            primary: true,
            drawer: true,
            order: { institution: 2, laboratory: 4, project: 5, personnel: 6 }[feature],
        }
        : undefined);
    const handle: AppRouteHandle = {
        ...existing,
        title,
        help: Object.prototype.hasOwnProperty.call(existing, "help") ? existing.help : null,
        access: Object.prototype.hasOwnProperty.call(existing, "access")
            ? existing.access
            : root ? { permission: root.permission } : routeAccess(topic, fullPath),
        breadcrumb,
        navigation,
    };
    return {
        ...route,
        handle,
        children: route.children ? withFeatureRouteMetadata(route.children, feature, fullPath) : undefined,
    } as AppRouteObject;
});

export const withInstitutionRouteMetadata = (routes: AppRouteObject[]): AppRouteObject[] => withFeatureRouteMetadata(routes, "institution");
export const withLaboratoryRouteMetadata = (routes: AppRouteObject[]): AppRouteObject[] => withFeatureRouteMetadata(routes, "laboratory");
export const withPersonnelRouteMetadata = (routes: AppRouteObject[]): AppRouteObject[] => withFeatureRouteMetadata(routes, "personnel");
export const withProjectRouteMetadata = (routes: AppRouteObject[]): AppRouteObject[] => withFeatureRouteMetadata(routes, "project");

export const withInstitutionRootMetadata = (route: AppRouteObject): AppRouteObject => withInstitutionRouteMetadata([route])[0];
export const withLaboratoryRootMetadata = (route: AppRouteObject): AppRouteObject => withLaboratoryRouteMetadata([route])[0];
export const withPersonnelRootMetadata = (route: AppRouteObject): AppRouteObject => withPersonnelRouteMetadata([route])[0];
export const withProjectRootMetadata = (route: AppRouteObject): AppRouteObject => withProjectRouteMetadata([route])[0];
